<style scoped>
.user-image {
    width: 50px;
    height: 50px;
}
.number-circle {
  border: 2px solid var(--z-primary);
  color: var(--z-primary);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  background-color: white;
  box-sizing: content-box;
}
</style>

<template>
    <div>
        <div class="d-flex align-center">
            <div class="title flex-grow-1">{{$translate('Team members')}}</div>
        </div>

        <div v-if="loading && !group" class="d-flex align-center justify-center">
            <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
        </div>
        <v-card v-else class="px-4 py-1">
            <div class="d-flex flex-row mt-1">
                <div class="font-weight-bold">{{$translate('Leaders')}}</div>
                <v-spacer/>
                <v-btn small icon @click="showSettings" v-if="isTeamLeader">
                    <v-icon color="primary">mdi-cog</v-icon>
                </v-btn>
                <teamsport-team-settings-dialog v-model="showSettingsDialog" :group="group" v-if="showSettingsDialog" />
            </div>
            <multi-column-list :items="sortedLeaders" class="ma-3">
                <template #icon="{item: user}">
                    <div style="position: relative; display: inline-flex;">
                        <v-avatar :size="20">
                            <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                        </v-avatar>
                        <v-icon
                            small
                            style="position: absolute; top: 0; left: 0; transform: translate(-50%, -50%);"
                            color="primary"
                            v-if="user.user_type == 'mainLeader'">
                            mdi-star
                        </v-icon>
                    </div>
                </template>
            </multi-column-list>
            <v-divider class="my-2" />
            <div class="d-flex flex-row">
                <div class="font-weight-bold flex-grow-1">{{$translate('Players')}}</div>
                <a href @click.prevent="setShirtNumbers" v-ripple="{center: true}" v-if="isTeamLeader">{{$translate('Set shirt numbers')}}</a>
                <teamsport-set-shirt-numbers-dialog v-model="showSetShirtNumbersDialog" :groupId="groupId" v-if="showSetShirtNumbersDialog" />
            </div>
            <multi-column-list :items="sortedPlayers" @click="showPlayerDetails" :clickable="isTeamLeader" class="ma-3">
                <template #icon="{item: user}">
                    <div class="d-flex">
                        <v-avatar :size="20">
                            <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                        </v-avatar>
                        <div class="number-circle ml-2" :style="{opacity: user.shirt_number ? 1 : 0}">{{ user.shirt_number }}</div>
                    </div>
                </template>
                <template #content="{item: user}">
                    {{user.name}}
                    <div v-if="membershipRequired">
                        <div v-if="!user.membership"><v-icon small color="error">mdi-account-off</v-icon>{{$translate('Missing membership')}}</div>
                        <div v-else-if="user.membership">{{ $translate('Valid to')}} {{ user.membershipToDate }}</div>
                    </div>
                </template>
                <template #append v-if="canInvitePlayers">
                    <div class="d-flex z-clickable" key="add" @click="invitePlayer" v-ripple="{center: true}">
                        <div class="mr-2">
                            <v-icon color="primary">mdi-plus-circle</v-icon>
                        </div>
                        <div>
                            <div>{{$translate('Invite players')}}</div>
                        </div>
                    </div>
                </template>
            </multi-column-list>
            <v-alert v-if="showMinNumberOfPlayersHint" type="warning" dense text class="mt-4" border="left">
                {{minNumberOfPlayersHint}}
            </v-alert>
        </v-card>
        <teamsport-invite-new-players-dialog v-model="showInvitePlayersDialog" :group="group" v-if="showInvitePlayersDialog" />
        <TeamsportPlayerDetailsDialog :user="playerDetails.user" v-if="playerDetails.show" v-model="playerDetails.show" :showAtX="750" :showAtY="500" />
    </div>
</template>

<script>
import MultiColumnList from '../components/Teamsport/MultiColumnList.vue';
import TeamsportSetShirtNumbersDialog from './TeamsportSetShirtNumbersDialog.vue';
import TeamsportTeamSettingsDialog from './TeamsportTeamSettingsDialog.vue';
import TeamsportInviteNewPlayersDialog from './TeamsportInviteNewPlayersDialog.vue';
import TeamsportPlayerDetailsDialog from '../components/Teamsport/PlayerDetailsDialog.vue';
export default {
    name: 'zoezi-teamsport-team-management',

    zoezi: {
        title: 'Manage team',
        icon: 'mdi-account-group',
        addon: 'TeamSports'
    },

    props: {
        registration: {
            title: 'Registration',
            type: Object
        }
    },

    components: {
        MultiColumnList,
        TeamsportTeamSettingsDialog,
        TeamsportSetShirtNumbersDialog,
        TeamsportInviteNewPlayersDialog,
        TeamsportPlayerDetailsDialog
    },
    data: () => ({
        loading: true,
        group: null,
        unsubscribe: [],
        showSettingsDialog: false,
        playerDetails: {
            show: false,
            user: null
        },
        showSetShirtNumbersDialog: false,
        showInvitePlayersDialog: false
    }),

    watch: {
        registration: {
            immediate: true,
            handler() {
                this.getGroup();
            }
        },
        '$store.state.user'() {
            this.getGroup();
        }
    },

    computed: {
        groupId() {
            return this.group?.id;
        },
        minNumberOfPlayersRequired() {
            return this.registration?.competition?.min_number_of_players;
        },
        membershipRequired() {
            return this.registration?.competition?.require_membership;
        },
        showMinNumberOfPlayersHint() {
            return this.minNumberOfPlayers && this.minNumberOfPlayers > this.group?.users?.length && this.canInvitePlayers;
        },
        minNumberOfPlayersHint() {
            return this.$translate('You need to invite atleast {0} more players to form a team').format(this.minNumberOfPlayers - this.group?.users?.length);
        },
        sortedLeaders() {
            if (!this.group) {
                return [];
            }
            return this.group.responsible.concat().sort((a, b) => a.name.localeCompare(b.name));
        },
        sortedPlayers() {
            if (!this.group) {
                return [];
            }
            return this.group.users.concat().sort((a, b) => a.name.localeCompare(b.name));
        },
        isTeamLeader() {
            return !!this.group?.responsible?.find(x => x.id == this.$store.state.user?.id);
        },
        hasMembership() {
            return this.$store.state?.settings?.membership;
        },
        canInvitePlayers() {
            return this.isTeamLeader || this.group?.memberCanInvite;
        }
    },

    methods: {
        getGroup() {
            this.loading = true;
            this.$api.get('/api/memberapi/group/get', {id: this.registration.group.id}).then(group => {
                group.users.forEach(x => {
                    x.shirt_number = group?.shirt_numbers?.find(y => y.user_id == x.id)?.shirt || null;
                })
                group.responsible.forEach(x => {
                    x.user_type = group?.user_types?.[x.id];
                })
                this.group = group;
                this.loading = false;
            });
        },
        invitePlayer() {
            this.showInvitePlayersDialog = true;
        },
        setShirtNumbers() {
            this.showSetShirtNumbersDialog = true;
        },
        showSettings() {
            this.showSettingsDialog = true;
        },
        showPlayerDetails({item: user}) {
            if (this.isTeamLeader) {
                this.playerDetails.show = true;
                this.playerDetails.user = this.playerDetails.show ? user : null;
            }
        }
    }
}
</script>
